import React from 'react';
import { BarChart2, CheckCircle2, X, XCircle } from 'lucide-react';

interface RunResultsProps {
  metrics: Record<string, unknown>;
  onClose: () => void;
  className?: string;
}

const formatValue = (value: unknown): string => {
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  if (Array.isArray(value)) return value.length ? value.map(formatValue).join(', ') : '—';
  if (value !== null && typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

/**
 * The last finished run's metrics over the simulation (WRK-20): what the lesson's supervisor wrote to metrics.json,
 * shown to everyone who runs the lesson, not only in a class's submissions. `success` becomes the headline.
 */
const RunResults: React.FC<RunResultsProps> = ({ metrics, onClose, className = 'top-3' }) => {
  const success = typeof metrics.success === 'boolean' ? metrics.success : null;
  const entries = Object.entries(metrics).filter(([key]) => key !== 'success');

  return (
    <div className={`absolute left-3 right-3 flex justify-start pointer-events-none ${className}`}>
      <section aria-label="Run results" role="status" aria-live="polite"
        className="pointer-events-auto max-w-md w-full max-h-[45vh] overflow-y-auto rounded-xl bg-gray-950/85 text-white shadow-lg backdrop-blur px-3 py-2.5">
        <div className="flex items-center gap-2 mb-2">
          <BarChart2 className="w-4 h-4 shrink-0 text-teal-300" aria-hidden="true" />
          <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-300">Run results</h2>
          {success !== null && (
            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${success ? 'bg-emerald-500/20 text-emerald-300' : 'bg-red-500/20 text-red-300'}`}>
              {success ? <CheckCircle2 className="w-3.5 h-3.5" aria-hidden="true" /> : <XCircle className="w-3.5 h-3.5" aria-hidden="true" />}
              {success ? 'Success' : 'Not yet'}
            </span>
          )}
          <button type="button" onClick={onClose} aria-label="Close the run results"
            className="ml-auto -mr-1 inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-300 hover:bg-white/10 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>
        {entries.length > 0 && (
          <dl className="flex flex-wrap gap-1.5">
            {entries.map(([key, value]) => (
              <div key={key} className="inline-flex items-center gap-1 rounded-md bg-white/10 px-2 py-1 text-xs">
                <dt className="text-gray-300">{key}:</dt>
                <dd className="font-semibold tabular-nums break-all">{formatValue(value)}</dd>
              </div>
            ))}
          </dl>
        )}
      </section>
    </div>
  );
};

export default RunResults;
